import {
  Box,
  Typography,
  Alert,
  Button,
  Stack,
  Divider,
  Card,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { TextEditedContent } from 'components';
import { AdminModal } from 'views/admin/components/styles';
import PropTypes from 'prop-types';
import logger from 'utils/logger';

const log = logger.createLogger('ConfirmContent');

const propTypes = {
  confirmModalOpen: PropTypes.bool.isRequired,
  setConfirmModalOpen: PropTypes.func.isRequired,
  responseData: PropTypes.object,
  handleCloseModal: PropTypes.func.isRequired,
  formState: PropTypes.object.isRequired,
  sendToServer: PropTypes.func.isRequired,
  setIncompleteWarningModalOpen: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
};

const ConfirmContent = ({
  confirmModalOpen,
  setConfirmModalOpen,
  responseData,
  handleCloseModal,
  formState,
  sendToServer,
  setIncompleteWarningModalOpen,
  isLoading,
}) => {
  const ERRORS = {
    NO_RESPONSE_DATA: {
      title: 'Error: No Response Data',
      message: 'No response data received from server.',
    },
    NO_RESULTS: {
      title: 'Error: No Results',
      message: 'No results found in server response.',
    },
  };

  const renderErrorContent = (error) => {
    return (
      <Box>
        <Typography variant="h6" mb={1}>
          {error.title}
        </Typography>
        <Alert severity="error" sx={{ mb: 1, py: 1 }}>
          {error.message}
        </Alert>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={handleCloseModal}
          style={{ color: 'white' }}>
          Close
        </Button>
      </Box>
    );
  };

  const renderResultsContent = (responseData) => {
    if (!responseData) {
      return renderErrorContent(ERRORS.NO_RESPONSE_DATA);
    }

    const { reportTemplate } = responseData;

    if (!reportTemplate) {
      return renderErrorContent(ERRORS.NO_RESULTS);
    }

    return (
      <Box minWidth={700}>
        <Typography variant="h6" mb={1}>
          Report Template Creation Results
        </Typography>

        {/* Summary Statistics */}
        <Box mb={1}>
          <Typography variant="subtitle2" fontWeight="bold" mb={1}>
            Summary:
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Alert severity="success" sx={{ mb: 1, py: 1 }}>
              {reportTemplate.id} has been created successfully.
            </Alert>
          </Stack>
        </Box>

        <Divider sx={{ mb: 1 }} />

        {/* Detailed Results */}
        <Box>
          <Grid container spacing={2} mb={2}>
            {/* Database Creation Result */}
            <Grid xs={6}>
              <Box sx={{ maxWidth: '100%', overflow: 'hidden' }}>
                <Typography variant="body2" fontWeight="bold">
                  Created Report Data:
                </Typography>
                <TextEditedContent
                  content={JSON.stringify(reportTemplate, undefined, 4)}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Box mt={2}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleCloseModal}
            style={{ color: 'white' }}>
            Close
          </Button>
        </Box>
      </Box>
    );
  };

  const renderConfirmContent = (formState) => {
    // Prepare the data to show in confirmation (same as what will be sent to server)
    const confirmData = { ...formState };
    const parsedConfirmData = JSON.parse(JSON.stringify(confirmData));

    // Parse the content field if it's a JSON string
    if (confirmData.content && typeof confirmData.content === 'string') {
      try {
        parsedConfirmData.content = JSON.parse(confirmData.content);
      } catch (error) {
        console.error('Failed to parse content JSON:', error);
      }
    }
    return (
      <Box>
        <Typography variant="h6" mb={1}>
          Confirm
        </Typography>
        <Typography>You are going to create a new report template:</Typography>
        <TextEditedContent
          content={JSON.stringify(parsedConfirmData, undefined, 4)}
        />
        <Grid spacing={3} container justifyContent="space-between">
          <Grid>
            <Button
              variant="contained"
              color="primary"
              style={{
                color: 'white',
              }}
              onClick={sendToServer}
              disabled={isLoading}>
              {isLoading ? 'Creating...' : 'Confirm'}
            </Button>
          </Grid>
          <Grid>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                setIncompleteWarningModalOpen(false);
                setConfirmModalOpen(false);
              }}
              disabled={isLoading}>
              Cancel
            </Button>
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <AdminModal
      open={confirmModalOpen}
      onClose={() => setConfirmModalOpen(false)}>
      <Card
        sx={{
          margin: 2,
          marginTop: 2,
          maxWidth: 1000,
          maxHeight: '90vh',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}>
        <Box py={2} px={2} sx={{ flex: 1, overflow: 'auto' }}>
          {responseData
            ? renderResultsContent(responseData)
            : renderConfirmContent(formState)}
        </Box>
      </Card>
    </AdminModal>
  );
};

ConfirmContent.propTypes = propTypes;
export default ConfirmContent;
